import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { preparativosAPI, emailResponsesAPI, messageTemplatesAPI } from '../../services/api';
import { getNetlifyFunctionUrl } from '../../config/netlify';
import * as m365EmailService from '../../services/m365EmailService';
import { supabase } from '../../config/supabase';
import { calcularEstadoYAvance } from '../../utils/preparativosHelpers';

// Async thunks
export const fetchPreparativo = createAsyncThunk(
  'preparativos/fetchPreparativo',
  async ({ temporadaId, week }, { rejectWithValue }) => {
    try {
      const response = await preparativosAPI.getBySeasonWeek({ temporadaId, week });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al obtener preparativo');
    }
  }
);

export const createPreparativo = createAsyncThunk(
  'preparativos/createPreparativo',
  async ({ temporadaId, week }, { rejectWithValue }) => {
    try {
      const response = await preparativosAPI.create({ temporadaId, week });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al crear preparativo');
    }
  }
);

export const updateStepStatus = createAsyncThunk(
  'preparativos/updateStepStatus',
  async ({ preparativoId, step, status }, { rejectWithValue, getState }) => {
    try {
      const response = await preparativosAPI.updateStepStatus({ preparativoId, step, status });
      
      // Calcular nuevo estado y avance basado en los datos actualizados
      const updatedPreparativo = response.data;
      const { estado, avance } = calcularEstadoYAvance(updatedPreparativo);
      
      // Actualizar estado y avance en la BD
      await preparativosAPI.updateEstadoYAvance({ preparativoId, estado, avance });
      
      return { step, status, data: { ...updatedPreparativo, estado, avance } };
    } catch (error) {
      return rejectWithValue(error.message || 'Error al actualizar estado');
    }
  }
);

export const updateEstadoYAvance = createAsyncThunk(
  'preparativos/updateEstadoYAvance',
  async ({ preparativoId, estado, avance }, { rejectWithValue }) => {
    try {
      const response = await preparativosAPI.updateEstadoYAvance({ preparativoId, estado, avance });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al actualizar estado y avance');
    }
  }
);

export const updateSolicitud = createAsyncThunk(
  'preparativos/updateSolicitud',
  async ({ preparativoId, solicitudData }, { rejectWithValue }) => {
    try {
      const response = await preparativosAPI.updateSolicitud({ preparativoId, solicitudData });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al actualizar solicitud');
    }
  }
);

export const updatePropuesta = createAsyncThunk(
  'preparativos/updatePropuesta',
  async ({ preparativoId, propuestaData }, { rejectWithValue }) => {
    try {
      const response = await preparativosAPI.updatePropuesta({ preparativoId, propuestaData });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al actualizar propuesta');
    }
  }
);

export const updateSAG = createAsyncThunk(
  'preparativos/updateSAG',
  async ({ preparativoId, sagData }, { rejectWithValue }) => {
    try {
      const response = await preparativosAPI.updateSAG({ preparativoId, sagData });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al actualizar agendamiento SAG');
    }
  }
);

export const updateTransporte = createAsyncThunk(
  'preparativos/updateTransporte',
  async ({ preparativoId, transporteData }, { rejectWithValue }) => {
    try {
      const response = await preparativosAPI.updateTransporte({ preparativoId, transporteData });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al actualizar transporte');
    }
  }
);

export const updateEtiquetas = createAsyncThunk(
  'preparativos/updateEtiquetas',
  async ({ preparativoId, etiquetasData }, { rejectWithValue }) => {
    try {
      const response = await preparativosAPI.updateEtiquetas({ preparativoId, etiquetasData });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al actualizar etiquetas');
    }
  }
);

// New KMS Etiquetas async thunks
export const solicitarEtiquetasKMS = createAsyncThunk(
  'preparativos/solicitarEtiquetasKMS',
  async (payload, { rejectWithValue }) => {
    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token;
      const url = getNetlifyFunctionUrl('solicitar-etiquetas-kms');
      
      console.log('🚀 Llamando a Netlify function:', url);
      console.log('📤 Payload:', payload);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      console.log('📥 Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
        let error;
        try {
          error = JSON.parse(errorText);
        } catch (e) {
          error = { error: errorText };
        }
        throw new Error(error.error || 'Error al solicitar etiquetas KMS');
      }

      const data = await response.json();
      console.log('✅ Response data:', data);
      return data;
    } catch (error) {
      console.error('💥 Error en solicitarEtiquetasKMS:', error);
      return rejectWithValue(error.message || 'Error al solicitar etiquetas KMS');
    }
  }
);

export const confirmarEtiquetas = createAsyncThunk(
  'preparativos/confirmarEtiquetas',
  async (preparativoId, { rejectWithValue }) => {
    try {
      const response = await preparativosAPI.updateStepStatus({ 
        preparativoId, 
        step: 'etiquetas', 
        status: 'Confirmado' 
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al confirmar etiquetas');
    }
  }
);

export const reintentarEtiquetas = createAsyncThunk(
  'preparativos/reintentarEtiquetas',
  async (_, { rejectWithValue, dispatch, getState }) => {
    try {
      // Get preparativo from current state
      const state = getState();
      let preparativo = state.preparativos.currentPreparativo;
      
      if (!preparativo) {
        throw new Error('No se encontró preparativo actual');
      }

      // Note: We don't reload preparativo here because fetchPreparativo requires {temporadaId, week}
      // and we only have the preparativo ID. The existing Redux state should have fresh data.
      // The component will reload after the retry completes using the correct parameters.
      console.log('🔄 Using existing preparativo data from Redux state for retry...');
      
      // Handle etiquetas_kms as either object or array, same as component logic
      console.log('🔍 Debug preparativo data:', preparativo);
      console.log('🔍 Raw etiquetas_kms:', preparativo.etiquetas_kms);
      
      const etiquetasKMS = Array.isArray(preparativo.etiquetas_kms) 
        ? preparativo.etiquetas_kms[0] 
        : preparativo.etiquetas_kms || null;
      
      console.log('🔍 Extracted etiquetasKMS:', etiquetasKMS);
      
      if (!etiquetasKMS) {
        console.error('❌ etiquetasKMS is null or undefined');
        throw new Error('No se encontró solicitud de etiquetas previa');
      }

      // Debug the structure of etiquetasKMS
      console.log('🔍 etiquetasKMS keys:', Object.keys(etiquetasKMS));
      console.log('🔍 awb_number:', etiquetasKMS.awb_number);
      console.log('🔍 shipment_date:', etiquetasKMS.shipment_date);
      console.log('🔍 products_json:', etiquetasKMS.products_json);
      console.log('🔍 consignee:', etiquetasKMS.consignee);
      console.log('🔍 truck_date:', etiquetasKMS.truck_date);
      console.log('🔍 carrier:', etiquetasKMS.carrier);

      // Build payload BEFORE clearing Redux state to ensure we have the data
      // Add isRetry flag to signal backend to delete existing record first
      let payload;
      
      if (Array.isArray(etiquetasKMS.products_json)) {
        // Multi-client format: products_json contains clients array
        payload = {
          preparativoId: preparativo.id,
          awb: etiquetasKMS.awb_number,
          date: etiquetasKMS.shipment_date,
          clients: etiquetasKMS.products_json,
          isRetry: true
        };
      } else {
        // Legacy format: products_json contains single client data
        payload = {
          preparativoId: preparativo.id,
          awb: etiquetasKMS.awb_number,
          date: etiquetasKMS.shipment_date,
          consignee: etiquetasKMS.consignee,
          truckDate: etiquetasKMS.truck_date,
          carrier: etiquetasKMS.carrier,
          products: etiquetasKMS.products_json?.products || etiquetasKMS.products_json,
          isRetry: true
        };
      }

      console.log('🚀 Retry payload constructed:', payload);

      // Validate payload has required fields before proceeding
      if (!payload.awb || !payload.date || (!payload.clients && !payload.products)) {
        console.error('❌ Invalid payload constructed:', payload);
        console.error('❌ Missing fields:');
        console.error('  - awb:', !!payload.awb);
        console.error('  - date:', !!payload.date);
        console.error('  - clients:', !!payload.clients);
        console.error('  - products:', !!payload.products);
        throw new Error('Datos insuficientes para reintentar - faltan AWB, fecha o productos');
      }

      // Now clear sync event data and reset polling state after payload construction
      // This ensures clean state between retry attempts
      dispatch(preparativosSlice.actions.clearSyncEventData());

      return await dispatch(solicitarEtiquetasKMS(payload)).unwrap();
    } catch (error) {
      return rejectWithValue(error.message || 'Error al reintentar');
    }
  }
);

export const fetchEstadoEmbarqueCierre = createAsyncThunk(
  'preparativos/fetchEstadoEmbarqueCierre',
  async ({ temporada, semana }, { rejectWithValue }) => {
    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token;
      const url = getNetlifyFunctionUrl('embarque-cierre');

      const response = await fetch(`${url}?temporada=${temporada}&semana=${semana}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Error al obtener el estado de cierre del embarque');
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message || 'Error al obtener el estado de cierre del embarque');
    }
  }
);

export const cerrarEmbarque = createAsyncThunk(
  'preparativos/cerrarEmbarque',
  async ({ temporada, semana }, { rejectWithValue }) => {
    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token;
      const url = getNetlifyFunctionUrl('embarque-cierre');

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ action: 'cerrar', temporada, semana })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al cerrar el embarque');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al cerrar el embarque');
    }
  }
);

export const reabrirEmbarque = createAsyncThunk(
  'preparativos/reabrirEmbarque',
  async ({ temporada, semana }, { rejectWithValue }) => {
    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token;
      const url = getNetlifyFunctionUrl('embarque-cierre');

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ action: 'reabrir', temporada, semana })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al reabrir el embarque');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al reabrir el embarque');
    }
  }
);

export const updateDUS = createAsyncThunk(
  'preparativos/updateDUS',
  async ({ preparativoId, dusData }, { rejectWithValue }) => {
    try {
      const response = await preparativosAPI.updateDUS({ preparativoId, dusData });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al actualizar DUS');
    }
  }
);

export const generarInstructivoEmbarque = createAsyncThunk(
  'preparativos/generarInstructivoEmbarque',
  async ({ preparativoId }, { rejectWithValue }) => {
    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token;
      const url = getNetlifyFunctionUrl('generar-instructivo-embarque');
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ preparativoId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al generar instructivo');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al generar Instructivo de Embarque');
    }
  }
);

export const enviarSolicitudDUS = createAsyncThunk(
  'preparativos/enviarSolicitudDUS',
  async ({ preparativoId, to, subject, message, documentFileId }, { rejectWithValue }) => {
    try {
      // Send email via M365 with document attachment
      await m365EmailService.sendEmail({
        to: to,
        subject: subject,
        body: message,
        documentFileId: documentFileId,
        preparativoId: preparativoId,
        stepType: 'solicitud_dus'
      });

      // Transition to esperando_respuesta state
      const token = (await supabase.auth.getSession()).data.session?.access_token;
      const url = getNetlifyFunctionUrl('dus-state-management');
      
      const stateResponse = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'transitionToEsperandoRespuesta',
          preparativoId: preparativoId
        })
      });

      if (!stateResponse.ok) {
        const errorData = await stateResponse.json();
        throw new Error(errorData.error || 'Error al actualizar estado');
      }

      return { success: true };
    } catch (error) {
      // Log failed send
      if (preparativoId) {
        await supabase
          .from('preparativos_email_log')
          .insert({
            preparativo_id: preparativoId,
            step_type: 'solicitud_dus',
            recipient_email: to,
            subject: subject,
            message: message,
            sent_at: new Date().toISOString(),
            status: 'failed',
            error_message: error.message
          });
      }

      return rejectWithValue(error.message || 'Error al enviar solicitud DUS');
    }
  }
);

export const confirmarDUSRecibido = createAsyncThunk(
  'preparativos/confirmarDUSRecibido',
  async ({ preparativoId, numeroDUS }, { rejectWithValue }) => {
    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token;
      const url = getNetlifyFunctionUrl('dus-state-management');
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'confirmarDUS',
          preparativoId: preparativoId,
          numeroDUS: numeroDUS
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al confirmar DUS');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al confirmar DUS recibido');
    }
  }
);

export const avanzarDUSSinCorreo = createAsyncThunk(
  'preparativos/avanzarDUSSinCorreo',
  async ({ preparativoId }, { rejectWithValue }) => {
    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token;
      const url = getNetlifyFunctionUrl('dus-state-management');

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'transitionToEsperandoRespuesta',
          preparativoId: preparativoId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al avanzar sin enviar correo');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al avanzar sin enviar correo');
    }
  }
);

export const updateAgenteAduanaInfo = createAsyncThunk(
  'preparativos/updateAgenteAduanaInfo',
  async ({ preparativoId, agenteAduanaCl, numeroDUSInicial, observaciones }, { rejectWithValue }) => {
    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token;
      const url = getNetlifyFunctionUrl('dus-state-management');
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'updateAgenteAduana',
          preparativoId: preparativoId,
          agenteAduanaCl: agenteAduanaCl,
          numeroDUSInicial: numeroDUSInicial,
          observaciones: observaciones
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al actualizar información');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al actualizar información del Agente de Aduana');
    }
  }
);

export const solicitarEmbarque = createAsyncThunk(
  'preparativos/solicitarEmbarque',
  async ({ preparativoId, to, subject, message }, { rejectWithValue }) => {
    try {
      // Send email via M365
      await m365EmailService.sendEmail({
        to: to,
        subject: subject,
        body: message,
        preparativoId: preparativoId,
        stepType: 'solicitud_embarque'
      });

      return { success: true };
    } catch (error) {
      // Log failed send
      if (preparativoId) {
        await supabase
          .from('preparativos_email_log')
          .insert({
            preparativo_id: preparativoId,
            step_type: 'solicitud_embarque',
            recipient_email: to,
            subject: subject,
            message: message,
            sent_at: new Date().toISOString(),
            status: 'failed',
            error_message: error.message
          });
      }

      return rejectWithValue(error.message || 'Error al solicitar embarque');
    }
  }
);

export const solicitarTransporte = createAsyncThunk(
  'preparativos/solicitarTransporte',
  async ({ preparativoId, to, subject, message }, { rejectWithValue }) => {
    try {
      // Send email via M365
      await m365EmailService.sendEmail({
        to: to,
        subject: subject,
        body: message,
        preparativoId: preparativoId,
        stepType: 'coordinar_transporte'
      });

      return { success: true };
    } catch (error) {
      // Log failed send
      if (preparativoId) {
        await supabase
          .from('preparativos_email_log')
          .insert({
            preparativo_id: preparativoId,
            step_type: 'coordinar_transporte',
            recipient_email: to,
            subject: subject,
            message: message,
            sent_at: new Date().toISOString(),
            status: 'failed',
            error_message: error.message
          });
      }

      return rejectWithValue(error.message || 'Error al solicitar transporte');
    }
  }
);

// Get email history for a preparativo step
export const getEmailHistory = createAsyncThunk(
  'preparativos/getEmailHistory',
  async ({ preparativoId, stepType }, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from('preparativos_email_log')
        .select('*')
        .eq('preparativo_id', preparativoId)
        .eq('step_type', stepType)
        .order('sent_at', { ascending: false });

      if (error) {
        throw new Error(error.message || 'Error al obtener historial de emails');
      }

      return { stepType, history: data || [] };
    } catch (error) {
      return rejectWithValue(error.message || 'Error al obtener historial de emails');
    }
  }
);

// Resend email manually
export const resendEmail = createAsyncThunk(
  'preparativos/resendEmail',
  async ({ preparativoId, stepType, to, subject, message }, { rejectWithValue }) => {
    try {
      // Send email via M365
      await m365EmailService.sendEmail({
        to: to,
        subject: subject,
        body: message,
        preparativoId: preparativoId,
        stepType: stepType
      });

      return { stepType };
    } catch (error) {
      // Log failed resend
      await supabase
        .from('preparativos_email_log')
        .insert({
          preparativo_id: preparativoId,
          step_type: stepType,
          recipient_email: to,
          subject: subject,
          message: message,
          sent_at: new Date().toISOString(),
          status: 'failed',
          error_message: error.message
        });

      return rejectWithValue(error.message || 'Error al reenviar email');
    }
  }
);

export const uploadDocument = createAsyncThunk(
  'preparativos/uploadDocument',
  async ({ temporada, semana, fileName, fileData, mimeType }, { rejectWithValue }) => {
    try {
      // Convert base64 to blob
      const byteCharacters = atob(fileData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });

      // Create file path: temporada/semana/fileName
      const filePath = `${temporada}/${semana}/${fileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('documentos-embarque')
        .upload(filePath, blob, {
          contentType: mimeType,
          upsert: true, // Overwrite if exists
        });

      if (error) {
        throw new Error(error.message || 'Error al subir documento a Supabase Storage');
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('documentos-embarque')
        .getPublicUrl(filePath);

      return {
        id: data.path,
        name: fileName,
        path: data.path,
        publicUrl: urlData.publicUrl,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Error al subir documento');
    }
  }
);

export const getDocument = createAsyncThunk(
  'preparativos/getDocument',
  async ({ temporada, semana, fileName }, { rejectWithValue }) => {
    try {
      // Create file path: temporada/semana/fileName
      const filePath = `${temporada}/${semana}/${fileName}`;

      // Check if file exists in Supabase Storage
      const { data: fileList, error: listError } = await supabase.storage
        .from('documentos-embarque')
        .list(`${temporada}/${semana}`, {
          search: fileName
        });

      if (listError) {
        throw new Error(listError.message || 'Error al buscar documento');
      }

      if (!fileList || fileList.length === 0) {
        throw new Error('Documento no encontrado');
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('documentos-embarque')
        .getPublicUrl(filePath);

      return {
        id: filePath,
        name: fileName,
        path: filePath,
        publicUrl: urlData.publicUrl,
        webViewLink: urlData.publicUrl, // For compatibility with existing code
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Error al obtener documento');
    }
  }
);

export const processDocument = createAsyncThunk(
  'preparativos/processDocument',
  async ({ preparativoId, fileId }, { rejectWithValue }) => {
    try {
      // Call Netlify function to process document via n8n
      const response = await fetch(getNetlifyFunctionUrl('process-document'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ fileId }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error al procesar documento');
      }

      const result = await response.json();
      const { awb, fecha_embarque } = result.data;

      // Save fileId, AWB, and shipping date to database
      await preparativosAPI.updateSolicitud({
        preparativoId,
        solicitudData: {
          documento_reserva_file_id: fileId,
          numero_awb: awb,
          fecha_embarque: fecha_embarque,
        },
      });

      return {
        fileId,
        awb,
        fecha_embarque,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Error al procesar documento');
    }
  }
);

// Guía de Despacho thunks
export const fetchGuiaDespacho = createAsyncThunk(
  'preparativos/fetchGuiaDespacho',
  async ({ preparativoId }, { rejectWithValue }) => {
    try {
      const response = await preparativosAPI.getGuiaDespacho({ preparativoId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al obtener guía de despacho');
    }
  }
);

export const solicitarNumeroGuia = createAsyncThunk(
  'preparativos/solicitarNumeroGuia',
  async ({ preparativoId }, { rejectWithValue }) => {
    try {
      const response = await preparativosAPI.solicitarNumeroGuia({ preparativoId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al solicitar número de guía');
    }
  }
);

export const confirmarNumeroGuia = createAsyncThunk(
  'preparativos/confirmarNumeroGuia',
  async ({ preparativoId, numeroGuia, syncEventId }, { rejectWithValue }) => {
    try {
      const response = await preparativosAPI.upsertGuiaDespacho({
        preparativoId,
        guiaData: {
          numero_guia: numeroGuia,
          sync_event_id: syncEventId,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al confirmar número de guía');
    }
  }
);

export const editarNumeroGuia = createAsyncThunk(
  'preparativos/editarNumeroGuia',
  async ({ preparativoId, numeroGuia }, { rejectWithValue }) => {
    try {
      const response = await preparativosAPI.upsertGuiaDespacho({
        preparativoId,
        guiaData: {
          numero_guia: numeroGuia,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al editar número de guía');
    }
  }
);

export const ingresarGuiaDespacho = createAsyncThunk(
  'preparativos/ingresarGuiaDespacho',
  async ({ preparativoId, detalleText }, { rejectWithValue }) => {
    try {
      const response = await preparativosAPI.ingresarGuiaDespacho({ preparativoId, detalleText });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al ingresar guía de despacho');
    }
  }
);

export const confirmarGuiaIngresada = createAsyncThunk(
  'preparativos/confirmarGuiaIngresada',
  async ({ preparativoId, oneDrivePdfUrl, oneDriveImageUrl, syncEventId }, { rejectWithValue }) => {
    try {
      const response = await preparativosAPI.confirmarGuiaIngresada({
        preparativoId,
        oneDrivePdfUrl,
        oneDriveImageUrl,
        syncEventId,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al confirmar guía ingresada');
    }
  }
);

export const ingresarFacturaLocal = createAsyncThunk(
  'preparativos/ingresarFacturaLocal',
  async ({ preparativoId, fecha }, { rejectWithValue }) => {
    try {
      const response = await preparativosAPI.ingresarFacturaLocal({ preparativoId, fecha });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al ingresar factura local');
    }
  }
);

export const confirmarFacturaLocalIngresada = createAsyncThunk(
  'preparativos/confirmarFacturaLocalIngresada',
  async ({ preparativoId, oneDrivePdfUrl, syncEventId, numero_factura, fecha }, { rejectWithValue }) => {
    try {
      const response = await preparativosAPI.confirmarFacturaLocalIngresada({
        preparativoId,
        oneDrivePdfUrl,
        syncEventId,
        numero_factura,
        fecha
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al confirmar factura local ingresada');
    }
  }
);

export const fetchFacturaLocalByPreparativo = createAsyncThunk(
  'preparativos/fetchFacturaLocalByPreparativo',
  async ({ preparativoId }, { rejectWithValue }) => {
    try {
      const response = await preparativosAPI.getFacturaLocalByPreparativo({ preparativoId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al obtener factura local');
    }
  }
);

export const fetchSyncEvent = createAsyncThunk(
  'preparativos/fetchSyncEvent',
  async ({ syncEventId }, { rejectWithValue }) => {
    try {
      const response = await preparativosAPI.getSyncEvent({ syncEventId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al obtener estado del evento');
    }
  }
);

export const fetchEmailStats = createAsyncThunk(
  'preparativos/fetchEmailStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await emailResponsesAPI.getStats();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al obtener estadísticas de emails');
    }
  }
);

export const rebajarInventarioPreparativo = createAsyncThunk(
  'preparativos/rebajarInventarioPreparativo',
  async ({ preparativoId }, { rejectWithValue }) => {
    try {
      const response = await preparativosAPI.rebajarInventarioPreparativo({ preparativoId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al rebajar inventario');
    }
  }
);

export const fetchMessageTemplate = createAsyncThunk(
  'preparativos/fetchMessageTemplate',
  async ({ stepType }, { rejectWithValue }) => {
    try {
      const response = await messageTemplatesAPI.getMessageTemplate(stepType);
      return { stepType, template: response.data };
    } catch (error) {
      return rejectWithValue(error.message || 'Error al obtener plantilla');
    }
  }
);

export const saveMessageTemplate = createAsyncThunk(
  'preparativos/saveMessageTemplate',
  async ({ stepType, messageTemplate }, { rejectWithValue }) => {
    try {
      const response = await messageTemplatesAPI.saveMessageTemplate(stepType, messageTemplate);
      return { stepType, template: response.data };
    } catch (error) {
      return rejectWithValue(error.message || 'Error al guardar plantilla');
    }
  }
);

const initialState = {
  currentPreparativo: null,
  loading: false,
  error: null,
  activeStep: 0,
  uploadingDocument: false,
  documentInfo: null,
  documentError: null,
  processingDocument: false,
  processedDocumentData: null,
  // Guía de Despacho state
  guiaDespacho: null,
  solicitandoGuia: false,
  guiaError: null,
  syncEvent: null,
  pollingSyncEvent: false,
  // Guía de Despacho Ingestion state
  ingresandoGuia: false,
  guiaIngresarError: null,
  syncEventIngresar: null,
  pollingSyncEventIngresar: false,
  // Factura Local state
  facturaLocal: null,
  ingresandoFacturaLocal: false,
  facturaLocalError: null,
  syncEventFacturaLocal: null,
  pollingSyncEventFacturaLocal: false,
  // Email tracking state
  emailHistory: {
    solicitud_embarque: [],
    coordinar_transporte: [],
    solicitud_dus: []
  },
  loadingEmailHistory: false,
  resendingEmail: false,
  // Email response stats
  emailStats: {
    successCount: 0,
    errorCount: 0,
    lastUpdated: null,
    lastEmail: null, // { step_type, temporada, semana, status, processed_at }
    loading: false,
    error: null
  },
  // Inventory deduction state
  rebajandoInventario: false,
  inventarioRebajadoSuccess: null,
  // Message templates state
  messageTemplates: {
    solicitud_embarque: null,
    agendamiento_sag: null,
    coordinar_transporte: null,
    solicitud_dus: null
  },
  loadingTemplate: false,
  templateError: null,
  // Cierre de Embarque state
  embarqueCierre: {
    estado: 'Abierto',
    fechaCierre: null,
    cerradoPor: null,
    // Propuesta anulada por exportación fallida (paso en 'Rechazado')
    exportacionFallida: false,
    anulacion: null,
    loading: false,
    error: null,
    actionLoading: false,
    actionError: null
  },
};

const preparativosSlice = createSlice({
  name: 'preparativos',
  initialState,
  reducers: {
    setActiveStep: (state, action) => {
      state.activeStep = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearDocumentState: (state) => {
      state.documentInfo = null;
      state.documentError = null;
    },
    resetPreparativo: (state) => {
      state.currentPreparativo = null;
      state.activeStep = 0;
      state.error = null;
      state.facturaLocal = null;
      state.ingresandoFacturaLocal = false;
      state.facturaLocalError = null;
      state.syncEventFacturaLocal = null;
      state.pollingSyncEventFacturaLocal = false;
    },
    clearDocumentError: (state) => {
      state.documentError = null;
    },
    clearGuiaError: (state) => {
      state.guiaError = null;
    },
    clearGuiaIngresarError: (state) => {
      state.guiaIngresarError = null;
    },
    clearFacturaLocalError: (state) => {
      state.facturaLocalError = null;
    },
    clearInventarioRebajadoSuccess: (state) => {
      state.inventarioRebajadoSuccess = null;
    },
    clearSyncEventData: (state) => {
      // Clear sync event data and reset polling state for retry cleanup
      // This ensures clean state between retry attempts
      state.isPolling = false;
      state.syncEventError = null;
      state.syncEventResults = null;
      // Note: Do NOT clear etiquetas_kms here as it's needed for retry payload construction
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch preparativo
      .addCase(fetchPreparativo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPreparativo.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPreparativo = action.payload;
        state.facturaLocal = null;
        state.ingresandoFacturaLocal = false;
        state.facturaLocalError = null;
        state.syncEventFacturaLocal = null;
        state.pollingSyncEventFacturaLocal = false;
      })
      .addCase(fetchPreparativo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create preparativo
      .addCase(createPreparativo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPreparativo.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPreparativo = action.payload;
      })
      .addCase(createPreparativo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update step status
      .addCase(updateStepStatus.fulfilled, (state, action) => {
        if (state.currentPreparativo) {
          const { step, status, data } = action.payload;
          
          // Mapeo de nombres de pasos a nombres de columnas en la BD
          const stepFieldMapping = {
            'solicitud': 'solicitud_embarque',
            'propuesta': 'propuesta_embarque',
            'sag': 'agendamiento_sag',
            'transporte': 'coordinar_transporte',
            'guia': 'guia_despacho',
            'etiquetas': 'impresion_etiquetas',
            'dus': 'solicitud_dus'
          };
          
          const mappedStep = stepFieldMapping[step] || step;
          const statusField = `estado_${mappedStep}`;
          const dateField = `fecha_actualizacion_${step}`;
          state.currentPreparativo[statusField] = status;
          state.currentPreparativo[dateField] = new Date().toISOString();
          
          // Actualizar estado y avance general
          if (data.estado) {
            state.currentPreparativo.estado = data.estado;
          }
          if (data.avance !== undefined) {
            state.currentPreparativo.avance = data.avance;
          }
        }
      })
      // Update estado y avance
      .addCase(updateEstadoYAvance.fulfilled, (state, action) => {
        if (state.currentPreparativo) {
          state.currentPreparativo.estado = action.payload.estado;
          state.currentPreparativo.avance = action.payload.avance;
        }
      })
      // Update solicitud
      .addCase(updateSolicitud.fulfilled, (state, action) => {
        if (state.currentPreparativo && state.currentPreparativo.solicitud) {
          state.currentPreparativo.solicitud = Array.isArray(action.payload) 
            ? action.payload[0] 
            : action.payload;
        }
      })
      // Update propuesta
      .addCase(updatePropuesta.fulfilled, (state, action) => {
        if (state.currentPreparativo && state.currentPreparativo.propuesta) {
          state.currentPreparativo.propuesta = Array.isArray(action.payload) 
            ? action.payload[0] 
            : action.payload;
        }
      })
      // Update SAG
      .addCase(updateSAG.fulfilled, (state, action) => {
        if (state.currentPreparativo && state.currentPreparativo.sag) {
          state.currentPreparativo.sag = Array.isArray(action.payload) 
            ? action.payload[0] 
            : action.payload;
        }
      })
      // Update transporte
      .addCase(updateTransporte.fulfilled, (state, action) => {
        if (state.currentPreparativo && state.currentPreparativo.transporte) {
          state.currentPreparativo.transporte = Array.isArray(action.payload) 
            ? action.payload[0] 
            : action.payload;
        }
      })
      // Update etiquetas
      .addCase(updateEtiquetas.fulfilled, (state, action) => {
        if (state.currentPreparativo && state.currentPreparativo.etiquetas) {
          state.currentPreparativo.etiquetas = Array.isArray(action.payload) 
            ? action.payload[0] 
            : action.payload;
        }
      })
      // Update DUS
      .addCase(updateDUS.fulfilled, (state, action) => {
        if (state.currentPreparativo && state.currentPreparativo.dus) {
          state.currentPreparativo.dus = Array.isArray(action.payload) 
            ? action.payload[0] 
            : action.payload;
        }
      })
      // Generar Instructivo de Embarque
      .addCase(generarInstructivoEmbarque.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generarInstructivoEmbarque.fulfilled, (state, action) => {
        state.loading = false;
        // Update DUS with new document info
        if (state.currentPreparativo && state.currentPreparativo.dus) {
          const dus = Array.isArray(state.currentPreparativo.dus) 
            ? state.currentPreparativo.dus[0] 
            : state.currentPreparativo.dus;
          if (dus) {
            dus.documento_onedrive_file_id = action.payload.fileId;
            dus.documento_generado_at = action.payload.generatedAt;
            dus.sub_estado = 'enviar_correo';
          }
        }
        if (state.currentPreparativo) {
          state.currentPreparativo.estado_solicitud_dus = 'En gestión';
        }
      })
      .addCase(generarInstructivoEmbarque.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Enviar Solicitud DUS
      .addCase(enviarSolicitudDUS.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(enviarSolicitudDUS.fulfilled, (state, action) => {
        state.loading = false;
        // Update sub_estado to esperando_respuesta
        if (state.currentPreparativo && state.currentPreparativo.dus) {
          const dus = Array.isArray(state.currentPreparativo.dus) 
            ? state.currentPreparativo.dus[0] 
            : state.currentPreparativo.dus;
          if (dus) {
            dus.sub_estado = 'esperando_respuesta';
          }
        }
      })
      .addCase(enviarSolicitudDUS.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Confirmar DUS Recibido
      .addCase(confirmarDUSRecibido.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(confirmarDUSRecibido.fulfilled, (state, action) => {
        state.loading = false;
        // Update status to Confirmado and clear sub_estado
        if (state.currentPreparativo && state.currentPreparativo.dus) {
          const dus = Array.isArray(state.currentPreparativo.dus) 
            ? state.currentPreparativo.dus[0] 
            : state.currentPreparativo.dus;
          if (dus) {
            dus.sub_estado = null;
            dus.numero_dus_inicial = action.payload.numeroDUS;
          }
        }
        if (state.currentPreparativo) {
          state.currentPreparativo.estado_solicitud_dus = 'Confirmado';
        }
      })
      .addCase(confirmarDUSRecibido.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Agente Aduana Info
      .addCase(updateAgenteAduanaInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAgenteAduanaInfo.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(updateAgenteAduanaInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Solicitar embarque
      .addCase(solicitarEmbarque.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(solicitarEmbarque.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(solicitarEmbarque.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Solicitar transporte
      .addCase(solicitarTransporte.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(solicitarTransporte.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(solicitarTransporte.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Upload document
      .addCase(uploadDocument.pending, (state) => {
        state.uploadingDocument = true;
        state.documentError = null;
      })
      .addCase(uploadDocument.fulfilled, (state, action) => {
        state.uploadingDocument = false;
        state.documentInfo = action.payload;
      })
      .addCase(uploadDocument.rejected, (state, action) => {
        state.uploadingDocument = false;
        state.documentError = action.payload;
      })
      // Get document
      .addCase(getDocument.pending, (state) => {
        state.loading = true;
        state.documentError = null;
      })
      .addCase(getDocument.fulfilled, (state, action) => {
        state.loading = false;
        state.documentInfo = action.payload;
      })
      .addCase(getDocument.rejected, (state, action) => {
        state.loading = false;
        state.documentError = action.payload;
      })
      // Process document
      .addCase(processDocument.pending, (state) => {
        state.processingDocument = true;
        state.error = null;
      })
      .addCase(processDocument.fulfilled, (state, action) => {
        state.processingDocument = false;
        state.processedDocumentData = action.payload;
        // Update current preparativo with AWB and shipping date
        if (state.currentPreparativo && state.currentPreparativo.solicitud) {
          const solicitud = Array.isArray(state.currentPreparativo.solicitud)
            ? state.currentPreparativo.solicitud[0]
            : state.currentPreparativo.solicitud;
          
          if (solicitud) {
            solicitud.documento_reserva_file_id = action.payload.fileId;
            solicitud.numero_awb = action.payload.awb;
            solicitud.fecha_embarque = action.payload.fecha_embarque;
          }
        }
      })
      .addCase(processDocument.rejected, (state, action) => {
        state.processingDocument = false;
        state.error = action.payload;
      })
      // Fetch guía de despacho
      .addCase(fetchGuiaDespacho.pending, (state) => {
        state.loading = true;
        state.guiaError = null;
      })
      .addCase(fetchGuiaDespacho.fulfilled, (state, action) => {
        state.loading = false;
        state.guiaDespacho = action.payload;
      })
      .addCase(fetchGuiaDespacho.rejected, (state, action) => {
        state.loading = false;
        state.guiaError = action.payload;
      })
      // Solicitar número de guía
      .addCase(solicitarNumeroGuia.pending, (state) => {
        state.solicitandoGuia = true;
        state.guiaError = null;
      })
      .addCase(solicitarNumeroGuia.fulfilled, (state, action) => {
        state.solicitandoGuia = false;
        // Store the sync event data for tracking
        if (state.guiaDespacho) {
          state.guiaDespacho.sync_event_id = action.payload.sync_event_id;
        }
      })
      .addCase(solicitarNumeroGuia.rejected, (state, action) => {
        state.solicitandoGuia = false;
        state.guiaError = action.payload;
      })
      // Confirmar número de guía
      .addCase(confirmarNumeroGuia.pending, (state) => {
        state.loading = true;
        state.guiaError = null;
      })
      .addCase(confirmarNumeroGuia.fulfilled, (state, action) => {
        state.loading = false;
        state.guiaDespacho = action.payload;
        // Update current preparativo
        if (state.currentPreparativo && state.currentPreparativo.guia_despacho) {
          state.currentPreparativo.guia_despacho = Array.isArray(action.payload)
            ? action.payload[0]
            : action.payload;
        }
      })
      .addCase(confirmarNumeroGuia.rejected, (state, action) => {
        state.loading = false;
        state.guiaError = action.payload;
      })
      // Editar número de guía
      .addCase(editarNumeroGuia.pending, (state) => {
        state.loading = true;
        state.guiaError = null;
      })
      .addCase(editarNumeroGuia.fulfilled, (state, action) => {
        state.loading = false;
        state.guiaDespacho = action.payload;
        // Update current preparativo
        if (state.currentPreparativo && state.currentPreparativo.guia_despacho) {
          state.currentPreparativo.guia_despacho = Array.isArray(action.payload)
            ? action.payload[0]
            : action.payload;
        }
      })
      .addCase(editarNumeroGuia.rejected, (state, action) => {
        state.loading = false;
        state.guiaError = action.payload;
      })
      // Ingresar guía de despacho
      .addCase(ingresarGuiaDespacho.pending, (state) => {
        state.ingresandoGuia = true;
        state.guiaIngresarError = null;
      })
      .addCase(ingresarGuiaDespacho.fulfilled, (state, action) => {
        state.ingresandoGuia = false;
        // Store the sync event data for tracking
        if (state.guiaDespacho) {
          state.guiaDespacho.sync_event_id_ingresar = action.payload.sync_event_id;
        }
      })
      .addCase(ingresarGuiaDespacho.rejected, (state, action) => {
        state.ingresandoGuia = false;
        state.guiaIngresarError = action.payload;
      })
      // Confirmar guía ingresada
      .addCase(confirmarGuiaIngresada.pending, (state) => {
        state.loading = true;
        state.guiaIngresarError = null;
      })
      .addCase(confirmarGuiaIngresada.fulfilled, (state, action) => {
        state.loading = false;
        state.guiaDespacho = action.payload;
        // Update current preparativo
        if (state.currentPreparativo && state.currentPreparativo.guia_despacho) {
          state.currentPreparativo.guia_despacho = Array.isArray(action.payload)
            ? action.payload[0]
            : action.payload;
        }
      })
      .addCase(confirmarGuiaIngresada.rejected, (state, action) => {
        state.loading = false;
        state.guiaIngresarError = action.payload;
      })
      // Factura Local
      .addCase(ingresarFacturaLocal.pending, (state) => {
        state.ingresandoFacturaLocal = true;
        state.facturaLocalError = null;
      })
      .addCase(ingresarFacturaLocal.fulfilled, (state, action) => {
        state.ingresandoFacturaLocal = false;
        // Inicializar facturaLocal con sync_event_id para comenzar polling
        if (!state.facturaLocal) {
          state.facturaLocal = {};
        }
        // El preparativo_id se estampa desde el argumento del thunk: la vista lo
        // usa para saber si esta fila le pertenece, y sin él una fila recién
        // creada aquí se descartaría por no poder atribuirla a nadie.
        state.facturaLocal.preparativo_id = action.meta.arg.preparativoId;
        state.facturaLocal.sync_event_id = action.payload.syncEventId;
        // Marcar que debe comenzar el polling
        state.pollingSyncEventFacturaLocal = true;
      })
      .addCase(ingresarFacturaLocal.rejected, (state, action) => {
        state.ingresandoFacturaLocal = false;
        state.facturaLocalError = action.payload;
      })
      .addCase(confirmarFacturaLocalIngresada.pending, (state) => {
        state.loading = true;
        state.facturaLocalError = null;
      })
      .addCase(confirmarFacturaLocalIngresada.fulfilled, (state, action) => {
        state.loading = false;
        state.facturaLocal = action.payload;
        if (state.currentPreparativo && state.currentPreparativo.factura_local) {
          state.currentPreparativo.factura_local = Array.isArray(action.payload)
            ? action.payload[0]
            : action.payload;
        }
      })
      .addCase(confirmarFacturaLocalIngresada.rejected, (state, action) => {
        state.loading = false;
        state.facturaLocalError = action.payload;
      })
      .addCase(fetchFacturaLocalByPreparativo.pending, (state) => {
        state.loading = true;
        state.facturaLocalError = null;
      })
      .addCase(fetchFacturaLocalByPreparativo.fulfilled, (state, action) => {
        state.loading = false;
        state.facturaLocal = action.payload;
      })
      .addCase(fetchFacturaLocalByPreparativo.rejected, (state, action) => {
        state.loading = false;
        state.facturaLocalError = action.payload;
      })
      // Fetch sync event
      .addCase(fetchSyncEvent.pending, (state, action) => {
        const target = action.meta?.arg?.target;
        if (target === 'ingresar') {
          state.pollingSyncEventIngresar = true;
          return;
        }
        if (target === 'factura-local-ingresar') {
          state.pollingSyncEventFacturaLocal = true;
          return;
        }
        state.pollingSyncEvent = true;
      })
      .addCase(fetchSyncEvent.fulfilled, (state, action) => {
        const target = action.meta?.arg?.target;
        if (target === 'ingresar') {
          state.pollingSyncEventIngresar = false;
          state.syncEventIngresar = action.payload;
          return;
        }
        if (target === 'factura-local-ingresar') {
          state.pollingSyncEventFacturaLocal = false;
          state.syncEventFacturaLocal = action.payload;
          return;
        }
        state.pollingSyncEvent = false;
        state.syncEvent = action.payload;
      })
      .addCase(fetchSyncEvent.rejected, (state, action) => {
        const target = action.meta?.arg?.target;
        if (target === 'ingresar') {
          state.pollingSyncEventIngresar = false;
          state.guiaIngresarError = action.payload;
          return;
        }
        if (target === 'factura-local-ingresar') {
          state.pollingSyncEventFacturaLocal = false;
          state.facturaLocalError = action.payload;
          return;
        }
        state.pollingSyncEvent = false;
        state.guiaError = action.payload;
      })
      // Get email history
      .addCase(getEmailHistory.pending, (state) => {
        state.loadingEmailHistory = true;
      })
      .addCase(getEmailHistory.fulfilled, (state, action) => {
        state.loadingEmailHistory = false;
        const { stepType, history } = action.payload;
        state.emailHistory[stepType] = history;
      })
      .addCase(getEmailHistory.rejected, (state, action) => {
        state.loadingEmailHistory = false;
        state.error = action.payload;
      })
      // Resend email
      .addCase(resendEmail.pending, (state) => {
        state.resendingEmail = true;
      })
      .addCase(resendEmail.fulfilled, (state, action) => {
        state.resendingEmail = false;
        // Email history will be refreshed by calling getEmailHistory after resend
      })
      .addCase(resendEmail.rejected, (state, action) => {
        state.resendingEmail = false;
        state.error = action.payload;
      })
      // Fetch email stats
      .addCase(fetchEmailStats.pending, (state) => {
        state.emailStats.loading = true;
        state.emailStats.error = null;
      })
      .addCase(fetchEmailStats.fulfilled, (state, action) => {
        state.emailStats.loading = false;
        state.emailStats.successCount = action.payload.success_count;
        state.emailStats.errorCount = action.payload.error_count;
        state.emailStats.lastUpdated = action.payload.last_updated;
        state.emailStats.lastEmail = action.payload.last_email;
      })
      .addCase(fetchEmailStats.rejected, (state, action) => {
        state.emailStats.loading = false;
        state.emailStats.error = action.payload;
      })
      // Rebajar inventario
      .addCase(rebajarInventarioPreparativo.pending, (state) => {
        state.rebajandoInventario = true;
        state.error = null;
        state.inventarioRebajadoSuccess = null;
      })
      .addCase(rebajarInventarioPreparativo.fulfilled, (state, action) => {
        state.rebajandoInventario = false;
        state.inventarioRebajadoSuccess = action.payload;
        // Update current preparativo with new inventory status
        if (state.currentPreparativo) {
          state.currentPreparativo.inventario_rebajado = true;
          state.currentPreparativo.fecha_rebaja_inventario = action.payload.fecha_rebaja;
        }
      })
      .addCase(rebajarInventarioPreparativo.rejected, (state, action) => {
        state.rebajandoInventario = false;
        state.error = action.payload;
        state.inventarioRebajadoSuccess = null;
      })
      // Fetch message template
      .addCase(fetchMessageTemplate.pending, (state) => {
        state.loadingTemplate = true;
        state.templateError = null;
      })
      .addCase(fetchMessageTemplate.fulfilled, (state, action) => {
        state.loadingTemplate = false;
        const { stepType, template } = action.payload;
        state.messageTemplates[stepType] = template;
      })
      .addCase(fetchMessageTemplate.rejected, (state, action) => {
        state.loadingTemplate = false;
        state.templateError = action.payload;
      })
      // Save message template
      .addCase(saveMessageTemplate.pending, (state) => {
        state.loadingTemplate = true;
        state.templateError = null;
      })
      .addCase(saveMessageTemplate.fulfilled, (state, action) => {
        state.loadingTemplate = false;
        const { stepType, template } = action.payload;
        state.messageTemplates[stepType] = template;
      })
      .addCase(saveMessageTemplate.rejected, (state, action) => {
        state.loadingTemplate = false;
        state.templateError = action.payload;
      })
      // Solicitar etiquetas KMS
      .addCase(solicitarEtiquetasKMS.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(solicitarEtiquetasKMS.fulfilled, (state, action) => {
        state.loading = false;
        // Update preparativo estado to "En gestión"
        if (state.currentPreparativo) {
          state.currentPreparativo.estado_impresion_etiquetas = 'En gestión';
          // Store sync event ID for polling
          if (!state.currentPreparativo.etiquetas_kms) {
            state.currentPreparativo.etiquetas_kms = [];
          }
          const etiquetasKMS = state.currentPreparativo.etiquetas_kms[0] || {};
          etiquetasKMS.sync_event_id = action.payload.syncEventId;
          state.currentPreparativo.etiquetas_kms[0] = etiquetasKMS;
        }
      })
      .addCase(solicitarEtiquetasKMS.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Confirmar etiquetas
      .addCase(confirmarEtiquetas.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(confirmarEtiquetas.fulfilled, (state, action) => {
        state.loading = false;
        // Update preparativo estado to "Confirmado"
        if (state.currentPreparativo) {
          state.currentPreparativo.estado_impresion_etiquetas = 'Confirmado';
        }
      })
      .addCase(confirmarEtiquetas.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Reintentar etiquetas
      .addCase(reintentarEtiquetas.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(reintentarEtiquetas.fulfilled, (state, action) => {
        state.loading = false;
        // Update sync event ID
        if (state.currentPreparativo && state.currentPreparativo.etiquetas_kms) {
          const etiquetasKMS = state.currentPreparativo.etiquetas_kms[0] || {};
          etiquetasKMS.sync_event_id = action.payload.syncEventId;
          state.currentPreparativo.etiquetas_kms[0] = etiquetasKMS;
        }
      })
      .addCase(reintentarEtiquetas.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Estado de cierre del embarque
      .addCase(fetchEstadoEmbarqueCierre.pending, (state) => {
        state.embarqueCierre.loading = true;
        state.embarqueCierre.error = null;
      })
      .addCase(fetchEstadoEmbarqueCierre.fulfilled, (state, action) => {
        state.embarqueCierre.loading = false;
        state.embarqueCierre.estado = action.payload.estado;
        state.embarqueCierre.fechaCierre = action.payload.fechaCierre;
        state.embarqueCierre.cerradoPor = action.payload.cerradoPor;
        state.embarqueCierre.exportacionFallida = !!action.payload.exportacionFallida;
        state.embarqueCierre.anulacion = action.payload.anulacion || null;
      })
      .addCase(fetchEstadoEmbarqueCierre.rejected, (state, action) => {
        state.embarqueCierre.loading = false;
        state.embarqueCierre.error = action.payload;
      })
      // Cerrar embarque
      .addCase(cerrarEmbarque.pending, (state) => {
        state.embarqueCierre.actionLoading = true;
        state.embarqueCierre.actionError = null;
      })
      .addCase(cerrarEmbarque.fulfilled, (state, action) => {
        state.embarqueCierre.actionLoading = false;
        state.embarqueCierre.estado = 'Cerrado';
        state.embarqueCierre.fechaCierre = action.payload.fechaCierre || state.embarqueCierre.fechaCierre;
        state.embarqueCierre.cerradoPor = action.payload.cerradoPor || state.embarqueCierre.cerradoPor;
      })
      .addCase(cerrarEmbarque.rejected, (state, action) => {
        state.embarqueCierre.actionLoading = false;
        state.embarqueCierre.actionError = action.payload;
      })
      // Reabrir embarque
      .addCase(reabrirEmbarque.pending, (state) => {
        state.embarqueCierre.actionLoading = true;
        state.embarqueCierre.actionError = null;
      })
      .addCase(reabrirEmbarque.fulfilled, (state) => {
        state.embarqueCierre.actionLoading = false;
        state.embarqueCierre.estado = 'Abierto';
        state.embarqueCierre.fechaCierre = null;
        state.embarqueCierre.cerradoPor = null;
      })
      .addCase(reabrirEmbarque.rejected, (state, action) => {
        state.embarqueCierre.actionLoading = false;
        state.embarqueCierre.actionError = action.payload;
      });
  },
});

export const { setActiveStep, clearError, resetPreparativo, clearDocumentError, clearDocumentState, clearGuiaError, clearGuiaIngresarError, clearFacturaLocalError, clearInventarioRebajadoSuccess } = preparativosSlice.actions;

// Selectors
export const selectCurrentPreparativo = (state) => state.preparativos.currentPreparativo;
export const selectPreparativosLoading = (state) => state.preparativos.loading;
export const selectPreparativosError = (state) => state.preparativos.error;
export const selectActiveStep = (state) => state.preparativos.activeStep;
export const selectUploadingDocument = (state) => state.preparativos.uploadingDocument;
export const selectDocumentInfo = (state) => state.preparativos.documentInfo;
export const selectDocumentError = (state) => state.preparativos.documentError;
export const selectProcessingDocument = (state) => state.preparativos.processingDocument;
export const selectProcessedDocumentData = (state) => state.preparativos.processedDocumentData;
export const selectGuiaDespacho = (state) => state.preparativos.guiaDespacho;
export const selectSolicitandoGuia = (state) => state.preparativos.solicitandoGuia;
export const selectGuiaError = (state) => state.preparativos.guiaError;
export const selectSyncEvent = (state) => state.preparativos.syncEvent;
export const selectPollingSyncEvent = (state) => state.preparativos.pollingSyncEvent;
export const selectIngresandoGuia = (state) => state.preparativos.ingresandoGuia;
export const selectGuiaIngresarError = (state) => state.preparativos.guiaIngresarError;
export const selectSyncEventIngresar = (state) => state.preparativos.syncEventIngresar;
export const selectPollingSyncEventIngresar = (state) => state.preparativos.pollingSyncEventIngresar;
export const selectFacturaLocal = (state) => state.preparativos.facturaLocal;
export const selectIngresandoFacturaLocal = (state) => state.preparativos.ingresandoFacturaLocal;
export const selectFacturaLocalError = (state) => state.preparativos.facturaLocalError;
export const selectSyncEventFacturaLocal = (state) => state.preparativos.syncEventFacturaLocal;
export const selectPollingSyncEventFacturaLocal = (state) => state.preparativos.pollingSyncEventFacturaLocal;
export const selectEmailHistory = (state) => state.preparativos.emailHistory;
export const selectLoadingEmailHistory = (state) => state.preparativos.loadingEmailHistory;
export const selectResendingEmail = (state) => state.preparativos.resendingEmail;
export const selectEmailStats = (state) => state.preparativos.emailStats;
export const selectRebajandoInventario = (state) => state.preparativos.rebajandoInventario;
export const selectInventarioRebajadoSuccess = (state) => state.preparativos.inventarioRebajadoSuccess;
export const selectMessageTemplates = (state) => state.preparativos.messageTemplates;
export const selectLoadingTemplate = (state) => state.preparativos.loadingTemplate;
export const selectTemplateError = (state) => state.preparativos.templateError;
export const selectEmbarqueCierre = (state) => state.preparativos.embarqueCierre;

// DUS-specific selectors
export const selectDUSSubEstado = (state) => {
  const dus = state.preparativos.currentPreparativo?.dus;
  return Array.isArray(dus) ? dus[0]?.sub_estado : dus?.sub_estado;
};
export const selectDUSDocumentoFileId = (state) => {
  const dus = state.preparativos.currentPreparativo?.dus;
  return Array.isArray(dus) ? dus[0]?.documento_onedrive_file_id : dus?.documento_onedrive_file_id;
};
export const selectDUSDocumentoGeneradoAt = (state) => {
  const dus = state.preparativos.currentPreparativo?.dus;
  return Array.isArray(dus) ? dus[0]?.documento_generado_at : dus?.documento_generado_at;
};

export default preparativosSlice.reducer;
